import { createFileRoute, Link, notFound, redirect, useRouter } from "@tanstack/react-router";
import { Sparkles, Flame } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { ShareButtons } from "@/components/ShareButtons";
import { BackButton } from "@/components/BackButton";
import { ArticleCard } from "@/components/ArticleCard";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { getCategoryBySlug, type DBCategory, type DBDiscovery, type DBQuiz } from "@/lib/content.functions";
import { resolveImage, absoluteImage } from "@/lib/asset-map";
import { SeasonFolders } from "@/components/seasons/SeasonFolders";
import { groupIntoSeriesFolders } from "@/lib/seasons";

// Click-worthy meta for category SERPs. Pages already rank pos 5-7 but get
// near-zero clicks - these titles/descriptions are tuned for CTR, not ranking.
const CATEGORY_SEO: Record<string, { title: string; description: string }> = {
  body: {
    title: "Human Body Science: The Strangest Machine You Own",
    description: "Your body breaks every rule biology class taught you. Surprising facts about the human body, from brain glitches to the cells you didn't know you had.",
  },
  ocean: {
    title: "Ocean Mysteries: What Really Lives in the Deep",
    description: "More of the ocean is unmapped than mapped. Meet the strangest creatures and unexplained mysteries hiding in Earth's deepest waters.",
  },
  mysteries: {
    title: "Unsolved Science Mysteries Experts Can't Explain",
    description: "Real phenomena science has measured, recorded - and still can't solve. Explore the strangest unexplained mysteries in modern science.",
  },
  "weird-animals": {
    title: "Weird Animals: Creatures That Shouldn't Exist",
    description: "Glass frogs, immortal jellyfish, and shrimp that punch faster than a bullet. Meet the strangest animals on the planet.",
  },
  space: {
    title: "Space Facts: Black Holes, Dying Stars & Diamond Rain",
    description: "The universe is stranger than fiction. Explore black holes, dying stars, and exoplanets where it literally rains diamonds.",
  },
  "ancient-earth": {
    title: "Ancient Earth: Dinosaurs, Extinctions & Lost Worlds",
    description: "The planet before us - dinosaurs, mass extinctions, and the strange Earth that existed long before humans showed up.",
  },
  "dangerous-nature": {
    title: "Dangerous Nature: When Earth Shows Its Teeth",
    description: "Storms, eruptions, and creatures so deadly evolution made them glow. The most dangerous things on Earth, explained simply.",
  },
  facts: {
    title: "Science Facts You Won't Unlearn",
    description: "The kind of science facts you immediately text to a friend. Surprising, weird, and impossible-to-forget discoveries.",
  },
  microscopic: {
    title: "Microscopic World: Tardigrades, Viruses & Hidden Life",
    description: "Smaller than you think. Tardigrades, viruses, and the invisible kingdoms of microscopic life living on (and inside) you.",
  },
  "mind-illusions": {
    title: "Mind & Illusions: Why Your Brain Lies to You",
    description: "Optical illusions, memory glitches, and colors that don't really exist. The strange ways your brain bends reality.",
  },
  "physics-weirdness": {
    title: "Physics Weirdness: Quantum, Time & Broken Reality",
    description: "Quantum entanglement, time dilation, and experiments that broke physics. Reality is stranger than fiction - here's why.",
  },
};


type CategoryRouteData = {
  category: DBCategory;
  discoveries: DBDiscovery[];
  otherCategories: DBCategory[];
  quizzes: DBQuiz[];
  featured: DBDiscovery[];
  latest: DBDiscovery[];
  popular: DBDiscovery[];
  trending: DBDiscovery[];
};

// Category slugs retired in the taxonomy cleanup that Google still crawls.
// Each points to its genuine successor so ranking signal is kept, not 404ed.
const RETIRED_CATEGORIES: Record<string, string> = {
  "mind-illusions": "/category/mind-psychology",
  "weird-animals": "/category/animals",
  "dangerous-nature": "/category/survival-disasters",
  "physics-weirdness": "/category/physics-chemistry",
  microscopic: "/collections/the-tiny-and-terrifying",
  mysteries: "/categories",
};

export const Route = createFileRoute("/category/$slug")({
  beforeLoad: ({ params }) => {
    const to = RETIRED_CATEGORIES[params.slug];
    if (to) throw redirect({ href: to, statusCode: 301 });
  },
  loader: async ({ params, context }) => {
    const data = await context.queryClient.ensureQueryData({
      queryKey: ["category", params.slug],
      queryFn: () => getCategoryBySlug({ data: { slug: params.slug } }),
    });
    if (!data) throw notFound();
    return data;
  },
  head: ({ params, loaderData }) => {
    const c = loaderData?.category;
    const firstDiscoveryImg = loaderData?.discoveries?.[0]?.image_url ?? null;
    const seo = CATEGORY_SEO[params.slug];
    const title = seo?.title ?? (c ? `${c.label || c.title} - Smartasaurus` : "Category · Smartasaurus");
    const desc =
      seo?.description ??
      c?.blurb ??
      "Dive into this Smartasaurus category - an endless rabbit hole of strange, real science discoveries and facts that sound made up.";
    const absImg = absoluteImage(c?.cover_url ?? firstDiscoveryImg);
    const url = `https://smartasaurus.com/category/${params.slug}`;
    const isEmpty = !loaderData?.discoveries || loaderData.discoveries.length === 0;
    const meta: Array<Record<string, string>> = [
      { title },
      { name: "description", content: desc },
      { property: "og:title", content: title },
      { property: "og:description", content: desc },
      { property: "og:type", content: "website" },
      { property: "og:url", content: url },
      { property: "og:site_name", content: "Smartasaurus" },
      { name: "twitter:card", content: absImg ? "summary_large_image" : "summary" },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: desc },
    ];
    if (isEmpty) {
      meta.push({ name: "robots", content: "noindex, follow" });
    }
    if (absImg) {
      meta.push({ property: "og:image", content: absImg });
      meta.push({ property: "og:image:alt", content: c?.title ?? c?.label ?? "Smartasaurus" });
      meta.push({ name: "twitter:image", content: absImg });
    }
    const scripts = c
      ? [
          {
            type: "application/ld+json",
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: "https://smartasaurus.com/" },
                { "@type": "ListItem", position: 2, name: c.label, item: url },
              ],
            }),
          },
          {
            type: "application/ld+json",
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "CollectionPage",
              name: c.title || c.label,
              description: c.blurb,
              url,
              mainEntity: {
                "@type": "ItemList",
                itemListElement: (loaderData?.discoveries ?? []).slice(0, 20).map((d, i) => ({
                  "@type": "ListItem",
                  position: i + 1,
                  url: `https://smartasaurus.com/discovery/${d.slug}`,
                  name: d.title,
                })),
              },
            }),
          },
        ]
      : undefined;
    return { meta, links: [{ rel: "canonical", href: url }], scripts };
  },
  component: CategoryPage,
  errorComponent: ({ error, reset }) => {
    const router = useRouter();
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-6 text-center">
        <div>
          <p className="text-muted-foreground">Couldn't load this category.</p>
          <button onClick={() => { router.invalidate(); reset(); }} className="mt-4 rounded-full bg-primary px-4 py-2 text-sm font-bold uppercase text-primary-foreground">Retry</button>
        </div>
      </div>
    );
  },
  notFoundComponent: () => (
    <div className="flex min-h-screen items-center justify-center bg-background p-6 text-center">
      <div>
        <h1 className="font-display text-6xl">404</h1>
        <p className="mt-2 text-muted-foreground">No such category.</p>
        <Link to="/" className="mt-4 inline-block rounded-full bg-primary px-4 py-2 text-sm font-bold uppercase text-primary-foreground">Back home</Link>
      </div>
    </div>
  ),
});

function CategoryPage() {
  const data = Route.useLoaderData() as CategoryRouteData;
  const { category, discoveries, otherCategories, quizzes, featured, latest, popular, trending } = data;
  const hero = featured[0] ?? latest[0];
  const featuredRest = featured.filter((d) => d.id !== hero?.id).slice(0, 4);
  const trendingItems = trending.slice(0, 5);
  const latestItems = latest.filter((d) => d.id !== hero?.id).slice(0, 6);
  const popularItems = popular
    .filter((d) => d.id !== hero?.id && !trendingItems.some((t) => t.id === d.id))
    .slice(0, 6);

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main id="main-content" className="mx-auto max-w-7xl px-4 py-16 md:px-8 md:py-24">
        <div className="mb-5 flex items-center gap-3">
          <BackButton fallback={{ to: "/categories" }} />
          <Breadcrumbs
            items={[
              { label: "Categories", to: "/categories" },
              { label: category.label, current: true },
            ]}
          />
        </div>
        <div className="border-b border-border pb-6">
          <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-primary">· Category</div>
          <h1 className="mt-2 font-display text-4xl uppercase leading-none md:text-6xl">{category.label}</h1>
          <p className="mt-1 text-xs font-bold uppercase tracking-widest text-muted-foreground">{category.title}</p>
          <p className="mt-3 max-w-2xl text-muted-foreground">{category.blurb}</p>
          <div className="mt-4 text-xs uppercase tracking-widest text-muted-foreground">
            {discoveries.length} stories in this hub
          </div>
        </div>

        {discoveries.length === 0 ? (
          <p className="mt-10 text-muted-foreground">No discoveries yet in this category.</p>
        ) : (
          <>
            {hero && (
              <section className="mt-10">
                <div className="mb-4 text-[10px] font-bold uppercase tracking-[0.3em] text-primary">· Featured</div>
                <div className="grid grid-cols-2 gap-3 sm:gap-6 md:grid-cols-3">
                  <div className="md:col-span-2">
                    <ArticleCard
                      slug={hero.slug}
                      img={resolveImage(hero.image_url)}
                      tag={category.label}
                      title={hero.title}
                      excerpt={hero.excerpt}
                      views={hero.views_label ?? undefined}
                      size="lg"
                    />
                  </div>
                  <div className="grid grid-cols-1 gap-6">
                    {featuredRest.slice(0, 2).map((d) => (
                      <ArticleCard
                        key={d.id}
                        slug={d.slug}
                        img={resolveImage(d.image_url)}
                        tag={category.label}
                        title={d.title}
                        excerpt={d.excerpt}
                        views={d.views_label ?? undefined}
                      />
                    ))}
                  </div>
                </div>
              </section>
            )}

            {trendingItems.length > 0 && (
              <section className="mt-16">
                <div className="mb-4 flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.3em] text-primary">
                  <Flame className="h-3 w-3" /> · Trending this week
                </div>
                <ol className="divide-y divide-border border-y border-border">
                  {trendingItems.map((d, i) => (
                    <li key={d.id}>
                      <Link
                        to="/discovery/$slug"
                        params={{ slug: d.slug }}
                        className="group flex items-center gap-4 py-4 transition hover:bg-card"
                      >
                        <span className="font-display text-2xl text-primary md:text-3xl">
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        <div className="min-w-0 flex-1">
                          <h3 className="font-display text-base uppercase leading-tight text-foreground group-hover:text-primary md:text-xl">
                            {d.title}
                          </h3>
                          <p className="mt-1 line-clamp-1 text-xs text-muted-foreground">{d.excerpt}</p>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ol>
              </section>
            )}

            {latestItems.length > 0 && (
              <section className="mt-16">
                <div className="mb-4 flex items-end justify-between">
                  <h2 className="font-display text-2xl uppercase md:text-3xl">Latest in {category.label}</h2>
                  <Link
                    to="/discoveries"
                    search={{ category: category.slug, sort: "newest" }}
                    className="text-xs font-bold uppercase tracking-widest text-muted-foreground hover:text-primary"
                  >
                    See all →
                  </Link>
                </div>
                <div className="grid grid-cols-2 gap-3 sm:gap-6 md:grid-cols-3">
                  {latestItems.map((d) => (
                    <ArticleCard
                      key={d.id}
                      slug={d.slug}
                      img={resolveImage(d.image_url)}
                      tag={category.label}
                      title={d.title}
                      excerpt={d.excerpt}
                      views={d.views_label ?? undefined}
                    />
                  ))}
                </div>
              </section>
            )}

            {popularItems.length > 0 && (
              <section className="mt-16">
                <div className="mb-4 flex items-end justify-between">
                  <h2 className="font-display text-2xl uppercase md:text-3xl">Most popular</h2>
                  <Link
                    to="/discoveries"
                    search={{ category: category.slug, sort: "popular" }}
                    className="text-xs font-bold uppercase tracking-widest text-muted-foreground hover:text-primary"
                  >
                    See all →
                  </Link>
                </div>
                <div className="grid grid-cols-2 gap-3 sm:gap-6 md:grid-cols-3">
                  {popularItems.map((d) => (
                    <ArticleCard
                      key={d.id}
                      slug={d.slug}
                      img={resolveImage(d.image_url)}
                      tag={category.label}
                      title={d.title}
                      excerpt={d.excerpt}
                      views={d.views_label ?? undefined}
                    />
                  ))}
                </div>
              </section>
            )}

            {discoveries.length > 5 && (
              <section className="mt-16">
                <div className="mb-2 text-[10px] font-bold uppercase tracking-[0.3em] text-primary">
                  · Browse by season
                </div>
                <h2 className="font-display text-2xl uppercase md:text-3xl">
                  Every episode in {category.label}
                </h2>
                <SeasonFolders
                  folders={groupIntoSeriesFolders(discoveries, [{ id: category.id, slug: category.slug }], { seasonSize: 10 })}
                  tagFor={() => category.label}
                />
              </section>
            )}
          </>

        )}

        {quizzes.length > 0 && (
          <section className="mt-16 border-t border-border pt-10">
            <div className="mb-4 flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.3em] text-primary">
              <Sparkles className="h-3 w-3" /> · Quizzes for {category.label}
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {quizzes.slice(0, 4).map((q) => (
                <Link
                  key={q.id}
                  to="/play"
                  search={{ quiz: q.slug }}
                  className="group rounded-2xl border border-border bg-card p-5 transition hover:border-primary/50"
                >
                  <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-primary">
                    {q.kind === "guess" ? "Guess First" : "Quick Challenge"}
                  </div>
                  <h3 className="mt-2 font-display text-lg uppercase leading-tight text-foreground group-hover:text-primary md:text-xl">
                    {q.question}
                  </h3>
                </Link>
              ))}
            </div>
          </section>
        )}

        {otherCategories.length > 0 && (
          <section className="mt-20 border-t border-border pt-10">
            <div className="mb-6 flex items-end justify-between">
              <h2 className="font-display text-2xl uppercase md:text-3xl">Explore other rabbit holes</h2>
              <Link to="/categories" className="text-xs font-bold uppercase tracking-widest text-muted-foreground hover:text-primary">
                All categories →
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
              {otherCategories.map((c) => (
                <Link
                  key={c.id}
                  to="/category/$slug"
                  params={{ slug: c.slug }}
                  className="group rounded-2xl border border-border bg-card p-5 transition hover:border-primary/50"
                >
                  <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-primary">· {c.label}</div>
                  <div className="mt-2 font-display text-lg uppercase leading-tight text-foreground group-hover:text-primary">
                    {c.title}
                  </div>
                  <p className="mt-2 line-clamp-2 text-xs text-muted-foreground">{c.blurb}</p>
                </Link>
              ))}
            </div>
          </section>
        )}
        <section className="border-t border-border py-8">
          <div className="mx-auto max-w-7xl px-4 md:px-8">
            <ShareButtons url={`https://smartasaurus.com/category/${category.slug}`} title={`${category.label} - Smartasaurus`} />
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}

